import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Pagination } from '../common/Pagination';
import { apiClient } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import {
  typesService,
  HardwareType,
  ModelReference,
  HardwareTypeCreate
} from '../../services/typesService';
import { HardwareTypeDetailModal } from './HardwareTypeDetailModal';
import { AddHardwareTypeModal } from './AddHardwareTypeModal';

interface HardwareTypesTableProps {
  canEdit?: boolean;
  refreshTrigger?: number;
  onTypeSelect?: (hardwareType: HardwareType) => void;
}

type ViewMode = 'grid' | 'list';

// Columns the backend can order by (see /v2/hardware-types ?sort_by=).
type HardwareSortField = 'code' | 'name' | 'device_count' | 'is_active' | 'updated_at';

export const HardwareTypesTable: React.FC<HardwareTypesTableProps> = ({
  canEdit = false,
  refreshTrigger,
  onTypeSelect
}) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { formatDate, t } = useI18n();
  // Soft-delete: admins can opt in to see deactivated hardware types. Default off.
  const [showInactive, setShowInactive] = useState(false);
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<Record<number, ModelReference[]>>({});
  // Initial-mount spinner vs background refetch indicator. The first
  // fetch swaps an empty page for content (full spinner is fine).
  // Search-debounced / pagination refetches should keep showing the
  // previous rows and signal staleness subtly — otherwise every
  // keystroke wipes the table to a spinner, which feels broken.
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Server-side ordering (matches DevicesTable): the backend honours
  // ?sort_by=&sort_dir= so sorting spans the whole catalogue, not just
  // the current page.
  const [sortField, setSortField] = useState<HardwareSortField>('code');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // The view-toggle UI was removed (2026-06-02) — the page renders the
  // list view exclusively now. The grid branch in the JSX below stays
  // around as a one-line revert if we ever bring the toggle back; the
  // `as ViewMode` cast keeps TS from narrowing the literal and
  // flagging the (currently unreachable) grid comparison.
  const viewMode = 'list' as ViewMode;
  // Server-side pagination state.
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(0);
  // Fleet-wide stats from /v2/hardware-types/stats. Computing from
  // `hardwareTypes.length` would silently undercount once pagination
  // is in play (the list endpoint only returns the current page).
  const [statsTotal, setStatsTotal] = useState<number | null>(null);
  const [statsActive, setStatsActive] = useState<number | null>(null);
  const [statsTotalModelRefs, setStatsTotalModelRefs] = useState<number | null>(null);

  // Modal states
  const [selectedType, setSelectedType] = useState<HardwareType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Debounce search input — pushed to the server-side `search` filter.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Reset to page 1 whenever a filter that changes the result-set
  // identity flips — otherwise we'd land on an empty page.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, showInactive, sortField, sortDirection]);

  // Fetch data with cleanup to prevent state updates on unmounted component.
  // Server-side paginated: ?page=N&limit=N&search=…&include_inactive=… —
  // backend returns { items, total, page, limit, pages }. Model refs
  // are fetched per current-page hw-type (O(pageSize), not O(total)).
  const fetchData = useCallback(async (isMounted: { current: boolean }) => {
    if (!isAuthenticated) {
      if (isMounted.current) setLoading(false);
      return;
    }

    try {
      if (isMounted.current) {
        // First fetch → full-page spinner. Subsequent fetches → keep
        // existing rows visible, surface a subtle "refetching" flag.
        if (hardwareTypes.length === 0) setLoading(true);
        else setRefetching(true);
        setError(null);
      }

      const includeInactive = showInactive && canSeeInactive;
      const query = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
      });
      if (includeInactive) query.set('include_inactive', 'true');
      // Backend search added in #96 — case-insensitive substring
      // match over code + name. Only send when non-empty so older
      // backends without the param see the same call they always did.
      const trimmedSearch = debouncedSearch.trim();
      if (trimmedSearch) query.set('search', trimmedSearch);
      query.set('sort_by', sortField);
      query.set('sort_dir', sortDirection);

      const response = await apiClient.get(`/v2/hardware-types?${query.toString()}`);
      if (!isMounted.current) return;

      // Response is either {items,total,page,limit,pages} (paginated
      // path) or a bare array (older deploys). Handle both.
      const items: HardwareType[] = Array.isArray(response)
        ? response
        : (response.items || []);
      const total: number = Array.isArray(response) ? items.length : (response.total ?? items.length);
      const pages: number = Array.isArray(response) ? 1 : (response.pages ?? 1);

      setHardwareTypes(items);
      setServerTotalItems(total);
      setServerTotalPages(pages);

      // Fetch model refs only for the current-page hardware types.
      const refsPromises = items.map((t: HardwareType) => typesService.getModelReferences(t.id, includeInactive));
      const refsResults = await Promise.all(refsPromises);
      if (!isMounted.current) return;

      const refsMap: Record<number, ModelReference[]> = {};
      items.forEach((t: HardwareType, i: number) => {
        refsMap[t.id] = refsResults[i];
      });
      setModelRefs(refsMap);
    } catch (err: unknown) {
      console.error('[HARDWARE TYPES TABLE] Error fetching data:', err);
      if (isMounted.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch hardware types';
        setError(errorMessage);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefetching(false);
      }
    }
  }, [isAuthenticated, showInactive, canSeeInactive, currentPage, itemsPerPage, debouncedSearch, sortField, sortDirection, hardwareTypes.length]);

  useEffect(() => {
    const isMounted = { current: true };
    fetchData(isMounted);
    return () => { isMounted.current = false; };
  }, [fetchData, refreshTrigger]);

  // Manual refresh function for use by handlers
  const refreshData = useCallback(() => {
    const isMounted = { current: true };
    fetchData(isMounted);
  }, [fetchData]);

  // Filter hardware types
  const filteredTypes = useMemo(() => {
    return hardwareTypes.filter((type) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        type.name.toLowerCase().includes(searchLower) ||
        type.code.toLowerCase().includes(searchLower) ||
        (type.description?.toLowerCase().includes(searchLower) ?? false)
      );
    });
  }, [hardwareTypes, searchTerm]);

  // Clicking a column header toggles direction when it's already the
  // active sort, otherwise switches to that column (ascending first) —
  // same behaviour as ClientsTable / DevicesTable.
  const handleSort = (field: HardwareSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: HardwareSortField) =>
    field !== sortField ? '↕️' : sortDirection === 'asc' ? '↑' : '↓';

  // Fetch fleet-wide stats from /v2/hardware-types/stats. Re-fires on
  // refresh trigger so create/edit/delete actions surface their effect.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/v2/hardware-types/stats');
        if (cancelled) return;
        setStatsTotal(res.total_types ?? null);
        setStatsActive(res.active_types ?? null);
        setStatsTotalModelRefs(res.total_model_refs ?? null);
      } catch (e: any) {
        // Best-effort — leave stats as null so they render as 0.
        // eslint-disable-next-line no-console
        console.warn('[HARDWARE TYPES TABLE] stats fetch failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, refreshTrigger]);

  // Fleet-wide stats (preferred) with a per-page fallback so the
  // cards never blank out while the stats request is in flight.
  const stats = useMemo(() => {
    const totalTypes = statsTotal ?? hardwareTypes.length;
    const activeTypes = statsActive ?? hardwareTypes.filter(t => t.is_active).length;
    const inactiveTypes = statsTotal != null && statsActive != null
      ? Math.max(0, statsTotal - statsActive)
      : hardwareTypes.filter(t => !t.is_active).length;
    const totalModelRefs = statsTotalModelRefs ?? Object.values(modelRefs).reduce((sum, refs) => sum + refs.length, 0);
    return { totalTypes, activeTypes, inactiveTypes, totalModelRefs };
  }, [hardwareTypes, modelRefs, statsTotal, statsActive, statsTotalModelRefs]);

  const handleCardClick = (type: HardwareType) => {
    setSelectedType(type);
    setIsModalOpen(true);
    if (onTypeSelect) {
      onTypeSelect(type);
    }
  };

  const handleCreateHardwareType = async (data: HardwareTypeCreate) => {
    await typesService.createHardwareType(data);
  };

  if (loading) {
    return (
      <div className="device-types-loading">
        <div className="loading-spinner"></div>
        <p>{t('deviceTypes.loadingHardwareTypes')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="device-types-error">
        <span className="error-icon">!</span>
        <h3>{t('deviceTypes.errorLoadingHardware')}</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={refreshData}>{t('errors.retryAction')}</button>
      </div>
    );
  }

  return (
    <div className="device-types-container hardware-types-container">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">🔧</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalTypes}</div>
            <div className="stat-label">{t('deviceTypes.hardwareTypes')}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✓</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeTypes}</div>
            <div className="stat-label">{t('common.active')}</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">⊘</div>
          <div className="stat-content">
            <div className="stat-value">{stats.inactiveTypes}</div>
            <div className="stat-label">{t('common.inactive')}</div>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalModelRefs}</div>
            <div className="stat-label">{t('deviceTypes.modelRefs')}</div>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <div className="device-types-section hardware-types-section">
        <div className="section-header">
          <div className="section-title">
            <h2>{t('deviceTypes.hardwareTypes')}</h2>
          </div>
          <div className="section-controls">
            {canEdit && (
              <button className="add-type-button" data-testid="btn-add-hardware-type" onClick={() => setShowAddModal(true)}>
                <span className="add-type-icon" aria-hidden="true">+</span>
                {t('deviceTypes.addHardwareType')}
              </button>
            )}
            <div className="search-container">
              <input
                type="text"
                data-testid="field-hardware-types-search"
                placeholder={t('deviceTypes.searchHardwareTypes')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input search-input--ota-style"
              />
            </div>
            {canSeeInactive && (
              <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                {t('common.showInactive')}
              </label>
            )}
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="device-types-grid hardware-types-grid">
            {filteredTypes.map((type) => {
              const refs = modelRefs[type.id] || [];
              return (
                <div
                  key={type.id}
                  className={`device-type-card hardware-type-card ${!type.is_active ? 'inactive' : ''}`}
                  data-testid={`row-hardware-type-${type.code}`}
                  onClick={() => handleCardClick(type)}
                >
                  <div className="card-header">
                    <span className="type-icon">{type.icon || '🔧'}</span>
                    <span className={`status-indicator ${type.is_active ? 'active' : 'inactive'}`} />
                  </div>

                  <div className="card-content">
                    <h3>{type.name}</h3>
                    <span className="type-code">{type.code}</span>
                    <p className="type-description">{type.description || t('deviceTypes.noDescription')}</p>
                  </div>

                  <div className="model-refs-preview" onClick={(e) => e.stopPropagation()}>
                    <div className="model-refs-badges">
                      {refs.slice(0, 3).map((ref) => (
                        <span key={ref.id} className="model-ref-badge">{ref.code}</span>
                      ))}
                      {refs.length > 3 && (
                        <span className="model-ref-more">+{refs.length - 3}</span>
                      )}
                      {refs.length === 0 && (
                        <span className="no-refs">{t('deviceTypes.noModelRefs')}</span>
                      )}
                    </div>
                  </div>

                  <div className="card-stats">
                    <div className="card-stat-item">
                      <span className="card-stat-value">{refs.length}</span>
                      <span className="card-stat-label">{t('deviceTypes.modelRefs')}</span>
                    </div>
                    <div className="card-stat-item">
                      <span className="card-stat-value">{type.device_count || 0}</span>
                      <span className="card-stat-label">{t('deviceTypes.devices')}</span>
                    </div>
                  </div>

                  <div className="card-footer">
                    <span className="last-updated">
                      {t('deviceTypes.updated')} {formatDate(type.updated_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="device-types-list hardware-types-list">
            <table className="device-types-table hardware-types-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    {t('deviceTypes.typeColumn')} {getSortIcon('name')}
                  </th>
                  <th onClick={() => handleSort('code')} className="sortable">
                    {t('deviceTypes.code')} {getSortIcon('code')}
                  </th>
                  <th>{t('deviceTypes.modelRefs')}</th>
                  <th onClick={() => handleSort('device_count')} className="sortable">
                    {t('deviceTypes.devices')} {getSortIcon('device_count')}
                  </th>
                  {showInactive && (
                    <th onClick={() => handleSort('is_active')} className="sortable">
                      {t('deviceTypes.statusColumn')} {getSortIcon('is_active')}
                    </th>
                  )}
                  <th onClick={() => handleSort('updated_at')} className="sortable">
                    {t('deviceTypes.updated')} {getSortIcon('updated_at')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTypes.map((type) => {
                  const refs = modelRefs[type.id] || [];
                  return (
                    <tr
                      key={type.id}
                      className={`device-type-row ${!type.is_active ? 'inactive-row' : ''}`}
                      data-testid={`row-hardware-type-${type.code}`}
                      onClick={() => handleCardClick(type)}
                    >
                      <td>
                        <div className="type-cell">
                          <span className="type-icon-small">{type.icon || '🔧'}</span>
                          <span className="type-name">{type.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="type-code">{type.code}</span>
                      </td>
                      <td className="model-refs-cell">
                        <div className="model-refs-inline">
                          {refs.slice(0, 3).map((ref) => (
                            <span key={ref.id} className="model-ref-badge small">{ref.code}</span>
                          ))}
                          {refs.length > 3 && (
                            <span className="model-ref-more">+{refs.length - 3}</span>
                          )}
                          {refs.length === 0 && (
                            <span className="no-refs">-</span>
                          )}
                        </div>
                      </td>
                      <td className="count-cell">{type.device_count || 0}</td>
                      {showInactive && (
                        <td>
                          <span className={`status-badge ${type.is_active ? 'active' : 'inactive'}`}>
                            {type.is_active ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                      )}
                      <td className="date-cell">
                        {formatDate(type.updated_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {filteredTypes.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-icon">🔧</div>
            <h3>{t('deviceTypes.noHardwareTypesFound')}</h3>
          </div>
        )}

        <Pagination
          page={currentPage}
          totalPages={serverTotalPages}
          totalItems={serverTotalItems}
          pageSize={itemsPerPage}
          itemLabel="hardware types"
          onPageChange={setCurrentPage}
          onPageSizeChange={(s) => { setItemsPerPage(s); setCurrentPage(1); }}
        />
      </div>

      {/* Activity Footer */}
      <div className="device-types-footer">
        <div className="activity-summary">
          <div className="activity-item">
            <span className="activity-icon">🔧</span>
            <span className="activity-text">
              <strong>{stats.totalTypes}</strong> hardware types
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">✓</span>
            <span className="activity-text">
              <strong>{stats.activeTypes}</strong> active
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">📋</span>
            <span className="activity-text">
              <strong>{stats.totalModelRefs}</strong> model references
            </span>
          </div>
        </div>
      </div>

      {/* Add Hardware Type Modal */}
      <AddHardwareTypeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={refreshData}
        onCreate={handleCreateHardwareType}
      />

      {/* Hardware Type Detail Modal */}
      <HardwareTypeDetailModal
        hardwareType={selectedType}
        modelRefs={selectedType ? (modelRefs[selectedType.id] || []) : []}
        isOpen={isModalOpen}
        canEdit={canEdit}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedType(null);
        }}
        onTypeUpdated={() => {
          setIsModalOpen(false);
          setSelectedType(null);
          refreshData();
        }}
      />
    </div>
  );
};
